import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import CustomNavbar from '../components/Navbar';
import { List, Pagination, Spin, Select, Empty } from 'antd';
import { BellOutlined, LoadingOutlined, MessageOutlined, AlertOutlined, InfoCircleOutlined, UserAddOutlined } from '@ant-design/icons';
import api from '../api/axios';
import { useProfile } from '../context/ProfileContext';

const { Option } = Select;

const TYPE_CONFIG = {
    MESSAGE:  { icon: <MessageOutlined />,  bg: 'linear-gradient(135deg,#0369A1,#0891B2)', label: 'Message' },
    ALERT:    { icon: <AlertOutlined />,    bg: 'linear-gradient(135deg,#DC2626,#EF4444)', label: 'Alert' },
    INVITE:   { icon: <UserAddOutlined />,  bg: 'linear-gradient(135deg,#059669,#10B981)', label: 'Invite' },
    INFO:     { icon: <InfoCircleOutlined />, bg: 'linear-gradient(135deg,#D97706,#F59E0B)', label: 'Info' },
};

const getConfig = (type) => TYPE_CONFIG[type] || { icon: <BellOutlined />, bg: 'linear-gradient(135deg,#880E6E,#AD1457)', label: type };

const Notification = () => {
    const { profileId } = useProfile();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [navbarReady, setNavbarReady] = useState(false);

    useEffect(() => {
        if (navbarReady) {
            fetchNotifications(currentPage, pageSize);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, navbarReady]);

    const fetchNotifications = async (page, size) => {
        if (!profileId) return;
        setLoading(true);
        try {
            const response = await api.get('/notifications/show', {
                params: { profile_id: profileId, page, limit: size },
            });
            if (response.data.status === 'success') {
                setNotifications(response.data.data.data);
                setTotal(response.data.data.total);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ fontFamily: 'Calibri, sans-serif' }}>
            <Helmet><title>Notifications | Oottan</title></Helmet>
            <CustomNavbar onNavbarReady={() => setNavbarReady(true)} />
            <div className="dashboard-wrapper">
                <div style={{ maxWidth: '860px', margin: '0 auto', padding: '0 16px 40px' }}>

                    {/* Header */}
                    <div style={{ textAlign: 'center', marginBottom: '32px', paddingTop: '8px' }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: '52px', height: '52px', borderRadius: '14px',
                            background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                            boxShadow: '0 6px 20px rgba(173,20,87,0.3)',
                            marginBottom: '14px',
                        }}>
                            <BellOutlined style={{ fontSize: '1.4rem', color: 'white' }} />
                        </div>
                        <div style={{
                            background: 'linear-gradient(135deg,#AD1457,#D81B60)',
                            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            fontSize: '1.6rem', fontWeight: '800', letterSpacing: '-0.3px',
                        }}>
                            Notifications
                        </div>
                        <div style={{ color: '#64748B', fontSize: '0.9rem', marginTop: '4px' }}>
                            {total > 0 ? `${total} notification${total !== 1 ? 's' : ''}` : 'All caught up'}
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 40, color: '#AD1457' }} spin />} />
                        </div>
                    ) : notifications.length === 0 ? (
                        <div style={{
                            background: 'white', borderRadius: '16px', padding: '60px 24px',
                            textAlign: 'center', border: '1px solid rgba(173,20,87,0.08)',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                        }}>
                            <Empty description={<span style={{ color: '#94A3B8' }}>No notifications yet</span>} />
                        </div>
                    ) : (
                        <>
                            <List
                                dataSource={notifications}
                                renderItem={(item, index) => {
                                    const cfg = getConfig(item.type);
                                    return (
                                        <List.Item style={{ padding: 0, marginBottom: '12px', border: 'none', display: 'block' }}>
                                            <div style={{
                                                background: 'white',
                                                borderRadius: '14px',
                                                padding: '16px 20px',
                                                border: '1px solid rgba(0,0,0,0.06)',
                                                boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                                                display: 'flex', alignItems: 'flex-start', gap: '14px',
                                                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                                                cursor: 'default',
                                            }}
                                                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.09)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                            >
                                                {/* Icon */}
                                                <div style={{
                                                    width: '42px', height: '42px', borderRadius: '12px', flexShrink: 0,
                                                    background: cfg.bg,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '1.1rem', color: 'white',
                                                    boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                                                }}>
                                                    {cfg.icon}
                                                </div>

                                                {/* Content */}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                                                        <span style={{
                                                            display: 'inline-block', padding: '2px 10px',
                                                            borderRadius: '9999px', fontSize: '0.72rem', fontWeight: '700',
                                                            background: cfg.bg, color: 'white', letterSpacing: '0.3px',
                                                        }}>
                                                            {cfg.label}
                                                        </span>
                                                        <span style={{ fontSize: '0.78rem', color: '#94A3B8', whiteSpace: 'nowrap' }}>
                                                            {new Date(item.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                    <div style={{ fontSize: '0.95rem', color: '#1A1A2E', lineHeight: '1.5' }}>
                                                        {item.message}
                                                    </div>
                                                </div>
                                            </div>
                                        </List.Item>
                                    );
                                }}
                            />

                            {/* Pagination */}
                            <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                flexWrap: 'wrap', gap: '12px',
                                background: 'white', borderRadius: '12px', padding: '14px 20px',
                                border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                            }}>
                                <Pagination
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={total}
                                    onChange={setCurrentPage}
                                    showSizeChanger={false}
                                />
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{ color: '#64748B', fontSize: '0.88rem' }}>Per page:</span>
                                    <Select value={pageSize} onChange={(v) => { setPageSize(v); setCurrentPage(1); }} style={{ width: '72px' }}>
                                        <Option value={5}>5</Option>
                                        <Option value={10}>10</Option>
                                        <Option value={15}>15</Option>
                                        <Option value={20}>20</Option>
                                    </Select>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Notification;
